import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { platformMeta } from '../platformMeta';
import { CalendarFreeSlot } from './calendarFreeSlots';
import { calendarMessages } from './calendarMessages';
import { formatPostTime } from './calendarPostView';
import { PlatformTile } from './PlatformTile';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AddOnDayMenuProps {
  day: Date;
  slots: CalendarFreeSlot[];
  /** Opens the Composer at a slot, or (null) on the day with no channel. */
  onPick: (day: Date, slot: CalendarFreeSlot | null) => void;
  /** The button shows always (the day panel), not only on hover. */
  isAlwaysVisible?: boolean;
}

/**
 * "+ Add" on a day (design b7): the free channel slots of the day at their best time, and
 * "Another time". A pick opens the Composer with the date, the time and the channel.
 */
const AddOnDayMenuComponent: React.FC<AddOnDayMenuProps> = ({ day, slots, onPick, isAlwaysVisible = false }) => {
  const intl = useIntl();
  const [isOpen, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((current) => !current), []);
  const dayLabel = intl.formatDate(day, { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <Dropdown isOpen={isOpen} toggle={toggle} className={isAlwaysVisible ? undefined : styles.addDay}>
      <DropdownToggle
        tag="button"
        type="button"
        className={styles.addDayButton}
        aria-label={intl.formatMessage(calendarMessages.addOnDay, { day: dayLabel })}
      >
        {intl.formatMessage(calendarMessages.add)}
      </DropdownToggle>
      <DropdownMenu end className={styles.pop}>
        <div className={styles.popHead}>
          {intl.formatMessage(
            { id: 'app.sml.promote.calendar.add.head', defaultMessage: '{day} · free slots' },
            { day: dayLabel }
          )}
        </div>
        {slots.map((slot) => (
          <DropdownItem key={slot.platform} className={styles.popOption} onClick={() => onPick(day, slot)}>
            <PlatformTile platform={slot.platform} size="md" />
            <span className={styles.popText}>
              <b>{`${platformMeta[slot.platform].name}, ${formatPostTime(intl.formatTime, slot.at)}`}</b>
              <small>
                {slot.isChannelBestTime
                  ? intl.formatMessage({
                      id: 'app.sml.promote.calendar.add.bestTime',
                      defaultMessage: 'Your best time',
                    })
                  : intl.formatMessage({
                      id: 'app.sml.promote.calendar.add.usualHour',
                      defaultMessage: 'The usual hour',
                    })}
              </small>
            </span>
          </DropdownItem>
        ))}
        <DropdownItem className={styles.popOption} onClick={() => onPick(day, null)}>
          <span className={styles.popPlus} aria-hidden="true">
            +
          </span>
          <span className={styles.popText}>
            <b>{intl.formatMessage({ id: 'app.sml.promote.calendar.add.other', defaultMessage: 'Another time' })}</b>
            <small>
              {intl.formatMessage({
                id: 'app.sml.promote.calendar.add.otherHint',
                defaultMessage: 'Write the post in the Composer',
              })}
            </small>
          </span>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const AddOnDayMenu = TypedMemo(AddOnDayMenuComponent);
